import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { ToastrService } from 'ngx-toastr';
import { TablerIconsModule } from 'angular-tabler-icons';
import { MaterialModule } from 'src/app/material.module';
import { ClinicalItemService } from 'src/app/pages/category/clinical-item/clinical-item.service';
import { DiagnosisCodingService } from 'src/app/services/diagnosis-coding.service';
import { IcdNode } from 'src/app/pages/category/clinical-item/iclinical-item';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DiagnosisSelection } from '../../iopddetails';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type DiagnosisPair = DiagnosisSelection;

@Component({
  selector: 'app-diagnosis-icd-dialog',
  standalone: true,
  imports: [CommonModule, MaterialModule, ReactiveFormsModule, TablerIconsModule,TranslateModule],
  templateUrl: './diagnosis-icd-dialog.component.html',
  styleUrl: './diagnosis-icd-dialog.component.scss'
})
export class DiagnosisIcdDialogComponent implements OnInit {
  @ViewChild('itemSelect') itemSelect?: MatSelect;

  form: FormGroup;
  categories: IcdNode[] = [];
  subcategories: IcdNode[] = [];
  items: IcdNode[] = [];
  selectedPairs: DiagnosisPair[] = [];
  isLoadingCategories = false;
  isLoadingSubcategories = false;
  isLoadingItems = false;
  isSaving = false;
  activeVersion = 'ICD';
  itemSearchControl = new FormControl('', { nonNullable: true });

  constructor(
    private formBuilder: FormBuilder,
    private dialogRef: MatDialogRef<DiagnosisIcdDialogComponent, DiagnosisSelection[]>,
    private itemService: ClinicalItemService,
    private diagnosisCodingService: DiagnosisCodingService,
    private toastr: ToastrService,
    private translate: TranslateService,
    private destroyRef: DestroyRef
  ) {
    this.form = this.formBuilder.group({
      category: [null, Validators.required],
      subcategory: [null, Validators.required],
      items: [[], Validators.required]
    });
  }

  ngOnInit(): void {
    this.loadConfiguration();
  }

  private loadConfiguration(): void {
    this.diagnosisCodingService.getConfiguration()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(configuration => {
        this.activeVersion = configuration.activeVersion;
        this.loadCategories();
      });
  }

  private loadCategories(): void {
    this.isLoadingCategories = true;
    this.itemService.getIcdCategories().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: categories => {
        this.categories = categories;
        this.isLoadingCategories = false;
      },
      error: () => {
        this.isLoadingCategories = false;
        this.toastr.error(this.translation('ICD.LOAD_CATEGORIES_ERROR'));
      }
    });
  }

  onCategoryChange(category: IcdNode): void {
    this.form.patchValue({ subcategory: null, items: [] });
    this.itemSearchControl.setValue('');
    this.subcategories = [];
    this.items = [];

    if (!category?.uri) {
      return;
    }

    this.isLoadingSubcategories = true;
    this.itemService.getIcdSubcategories(category.uri).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: subcategories => {
        this.subcategories = subcategories;
        this.isLoadingSubcategories = false;
      },
      error: () => {
        this.isLoadingSubcategories = false;
        this.toastr.error(this.translation('ICD.LOAD_SUBCATEGORIES_ERROR'));
      }
    });
  }

  onSubcategoryChange(subcategory: IcdNode): void {
    this.form.patchValue({ items: [] });
    this.itemSearchControl.setValue('');
    this.items = [];

    if (!subcategory?.uri) {
      return;
    }

    const category = this.form.get('category')?.value as IcdNode | null;
    this.isLoadingItems = true;
    this.itemService.getIcdItems(subcategory.uri, category?.uri).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: items => {
        this.items = items;
        this.isLoadingItems = false;
      },
      error: () => {
        this.isLoadingItems = false;
        this.toastr.error(this.translation('ICD.LOAD_ITEMS_ERROR'));
      }
    });
  }

  addPair(): void {
    if (this.form.invalid) {
      return;
    }

    const category = this.form.get('category')?.value as IcdNode;
    const subcategory = this.form.get('subcategory')?.value as IcdNode;
    const selectedItems = this.form.get('items')?.value as IcdNode[];
    const existingIds = new Set(this.selectedPairs.map(pair => pair.item.id));
    const pairs = selectedItems
      .filter(item => item?.id && !existingIds.has(item.id))
      .map(item => ({ category, subcategory, item }));

    this.selectedPairs = [...this.selectedPairs, ...pairs];
    this.clearItemSelection();
  }

  get filteredItems(): IcdNode[] {
    const query = this.itemSearchControl.value.trim().toLowerCase();
    if (!query) {
      return this.items;
    }

    return this.items.filter(item => this.displayNode(item).toLowerCase().includes(query));
  }

  get areAllFilteredItemsSelected(): boolean {
    if (this.filteredItems.length === 0) {
      return false;
    }

    const selectedKeys = new Set(this.selectedItems.map(item => this.itemKey(item)));
    return this.filteredItems.every(item => selectedKeys.has(this.itemKey(item)));
  }

  get areSomeFilteredItemsSelected(): boolean {
    if (this.filteredItems.length === 0 || this.areAllFilteredItemsSelected) {
      return false;
    }

    const selectedKeys = new Set(this.selectedItems.map(item => this.itemKey(item)));
    return this.filteredItems.some(item => selectedKeys.has(this.itemKey(item)));
  }

  toggleSelectAllItems(checked: boolean): void {
    const filteredKeys = new Set(this.filteredItems.map(item => this.itemKey(item)));
    let nextItems: IcdNode[];

    if (checked) {
      const selectedKeys = new Set(this.selectedItems.map(item => this.itemKey(item)));
      const newItems = this.filteredItems.filter(item => !selectedKeys.has(this.itemKey(item)));
      nextItems = [...this.selectedItems, ...newItems];
    } else {
      nextItems = this.selectedItems.filter(item => !filteredKeys.has(this.itemKey(item)));
    }

    this.form.get('items')?.setValue(nextItems);
    this.form.get('items')?.markAsDirty();
  }

  onItemsDropdownOpenedChange(opened: boolean): void {
    if (!opened) {
      this.itemSearchControl.setValue('');
    }
  }

  removePair(index: number): void {
    this.selectedPairs = this.selectedPairs.filter((_, itemIndex) => itemIndex !== index);
  }

  get hasSelectedPairs(): boolean {
    return this.selectedPairs.length > 0;
  }

  save(): void {
    const selectedItems = this.selectedPairs
      .map(({ category, subcategory, item }) => ({ category, subcategory, item }));

    if (selectedItems.length === 0) {
      this.toastr.error(this.translation('ICD.SELECT_ITEM_ERROR'));
      return;
    }

    this.isSaving = true;
    this.dialogRef.close(selectedItems);
  }

  close(): void {
    this.dialogRef.close();
  }

  trackByPair(_index: number, pair: DiagnosisPair): number {
    return pair.item.id || 0;
  }

  displayNode(node: IcdNode): string {
    if (!node) {
      return '';
    }

    return node.code ? `${node.code} - ${node.name}` : node.name || '';
  }

  private get selectedItems(): IcdNode[] {
    return (this.form.get('items')?.value || []) as IcdNode[];
  }

  private clearItemSelection(): void {
    const itemsControl = this.form.get('items');
    itemsControl?.setValue([]);
    itemsControl?.markAsPristine();
    itemsControl?.markAsUntouched();
    this.itemSelect?.options.forEach(option => {
      if (option.selected) {
        option.deselect();
      }
    });
    this.itemSearchControl.setValue('');
  }

  private itemKey(item: IcdNode): string | number {
    return item.id ?? item.uri ?? `${item.code || ''}|${item.name || ''}`;
  }

  private translation(key: string): string {
    return this.translate.instant(key, { version: this.activeVersion });
  }

}
